import {
  Children,
  type ReactElement,
  type SetStateAction,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { NavigateOptions } from "react-router";
import {
  useRemoveSearchParams,
  useSearchParamState,
} from "src/hooks/useSearchParams";
import Tab from "./components/Tab/Tab";
import { DEFAULT_TAB_SEARCH_PARAM } from "./Tabs.constants";
import type { TabProps, TabsProps, TabsSearchParam } from "./Tabs.props";

type UseTabsStateReturn =
  | [
      string | undefined,
      (value: SetStateAction<string | undefined>, options?: NavigateOptions) => void,
    ]
  | [
      string | null | undefined,
      (newValue?: string | null | undefined, options?: NavigateOptions) => void,
    ];

export const useTabsState = ({
  searchParam,
  searchParamOpts,
  value,
  cleanupSearchParamsByTab,
}: {
  searchParam?: TabsSearchParam;
  searchParamOpts?: NavigateOptions;
  value?: string;
  cleanupSearchParamsByTab?: Record<string, readonly string[]>;
}): UseTabsStateReturn => {
  const removeSearchParam = useRemoveSearchParams();

  // in case "searchParam" is not set, the tabs use an internal state to store the selected tab, and aren't syncing with the URL search params
  const localState = useState<string | undefined>(value);

  // when `searchParam` prop is used without a value, is it treated as "true",
  // which means these tabs use the default search param name to sync with the URL
  const searchParamName =
    searchParam === true ? DEFAULT_TAB_SEARCH_PARAM : searchParam;

  // when the `searchParamName` prop is ommited from the `Tabs` component or set to `false`
  // the tabs use an internal state to store the selected tab, and aren't syncing with the URL search params
  const [selectedTab, setSelectedTab] = useSearchParamState(
    typeof searchParamName === "string" ? (searchParamName as string) : undefined,
    value,
    searchParamOpts
  );

  const currentTabNameRef = useRef<string | undefined>(selectedTab as string);

  useEffect(() => {
    const currentTabName = currentTabNameRef.current;
    const cleanupSearchParams = cleanupSearchParamsByTab?.[currentTabName || ""];

    if (currentTabName && currentTabName !== selectedTab && cleanupSearchParams) {
      removeSearchParam([...cleanupSearchParams]);
    }

    currentTabNameRef.current = selectedTab as string;
  }, [selectedTab]);

  return searchParam
    ? [selectedTab as string | undefined, setSelectedTab]
    : localState;
};

export const useTabs = (
  children: TabsProps["children"],
  searchParam?: TabsSearchParam,
  _onChange?: (v: string) => void,
  value?: string,
  cleanupSearchParamsByTab?: Record<string, readonly string[]>
) => {
  const [selectedTab, setSelectedTab] = useTabsState({
    searchParam,
    value,
    cleanupSearchParamsByTab,
  });

  const { tabsChildren, otherChildren } = useMemo(() => {
    const arrayChildren = Children.toArray(children) as ReactElement<TabProps>[];

    return arrayChildren.reduce(
      (acc, child) => {
        (child.type === Tab ? acc.tabsChildren : acc.otherChildren).push(child);
        return acc;
      },
      {
        tabsChildren: [] as ReactElement<TabProps>[],
        otherChildren: [] as ReactElement<any>[],
      }
    );
  }, [children]);

  // by filtering out the disabled tabs (in the "tabIds" code),
  // ensures an initial selection of the first non-disabled tab
  const tabIds = useMemo(
    () =>
      tabsChildren.filter((child) => !child.props.disabled).map((c) => c.props.id),
    [tabsChildren]
  );

  // map all possible tabs' aliases to their ids
  const aliasMappedToIds = useMemo(
    () =>
      tabsChildren.reduce<Record<string, string>>((acc, child) => {
        const aliases = [child.props.alias].flat().filter(Boolean) as string[];
        aliases.forEach((alias) => (acc[alias] = child.props.id));
        return acc;
      }, {}),
    [tabsChildren]
  );

  // the default tab is the first non-disabled tab with a label.
  // label-less tabs can only be accessed by url and have no actual rendered "tab" (only tab content)
  const getDefaultTabId = useMemo(
    () =>
      tabsChildren.find((child) => !child.props.disabled && !!child.props.label)
        ?.props.id,
    [tabsChildren]
  );

  // a caller-supplied `value` overrides the first tab as the default (e.g. a
  // tenant-configured landing tab), as long as it names an enabled tab
  const defaultTabId = useMemo(
    () => (value && tabIds.includes(value) ? value : getDefaultTabId),
    [value, tabIds, getDefaultTabId]
  );

  const selectedTabId = useMemo(() => {
    if (!selectedTab) return defaultTabId;

    return (
      aliasMappedToIds[selectedTab] ||
      (tabIds.includes(selectedTab) ? selectedTab : defaultTabId)
    );
  }, [selectedTab, tabIds, defaultTabId, aliasMappedToIds]);

  // sync default tab to URL when param is missing (so URL reflects actual state)
  // This makes getting the tab value simpler for places like the AI toolbox section
  useEffect(() => {
    if (searchParam && defaultTabId && !selectedTab) {
      setSelectedTab(defaultTabId, { replace: true });
    }
  }, [searchParam, defaultTabId, selectedTab, setSelectedTab]);

  const onChange = useCallback(
    (_e: SyntheticEvent, v: string) => {
      setSelectedTab(v);
      _onChange && _onChange?.(v);
    },
    [setSelectedTab, _onChange]
  );

  return {
    tabsChildren,
    otherChildren,
    selectedTabId,
    onChange,
  };
};
